// AAC Dictionary with vocabulary-based prediction
import { 
  detectLanguage as detectLang, 
  searchVocabulary, 
  type SupportedLanguage 
} from "./vocabulary";

export interface DictionaryEntry {
  id: string;
  text: string;
  acronym: string;
  language: SupportedLanguage;
  frequency: number;
}

// Generate acronym from text (first letter of each word)
export function generateAcronym(text: string): string {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toLowerCase())
    .join("");
}

// Re-export detectLanguage for backward compatibility
export function detectLanguage(text: string): SupportedLanguage {
  return detectLang(text);
}

// Common AAC phrases as seed entries (kept for phrase completion)
export const seedDictionary: DictionaryEntry[] = [
  // English phrases
  { id: "1", text: "I want water", acronym: "iww", language: "en", frequency: 100 },
  { id: "2", text: "I need help", acronym: "inh", language: "en", frequency: 95 },
  { id: "3", text: "Thank you", acronym: "ty", language: "en", frequency: 90 },
  { id: "4", text: "How are you", acronym: "hay", language: "en", frequency: 85 },
  { id: "5", text: "I love you", acronym: "ily", language: "en", frequency: 80 },
  { id: "6", text: "Good morning", acronym: "gm", language: "en", frequency: 75 },
  { id: "7", text: "Good night", acronym: "gn", language: "en", frequency: 70 },
  { id: "8", text: "I want food", acronym: "iwf", language: "en", frequency: 65 },
  { id: "9", text: "I feel pain", acronym: "ifp", language: "en", frequency: 60 },
  { id: "10", text: "Please wait", acronym: "pw", language: "en", frequency: 55 },
  { id: "11", text: "I am tired", acronym: "iat", language: "en", frequency: 50 },
  { id: "12", text: "Yes please", acronym: "yp", language: "en", frequency: 45 },
  { id: "13", text: "No thank you", acronym: "nty", language: "en", frequency: 40 },
  { id: "14", text: "Call my family", acronym: "cmf", language: "en", frequency: 35 },
  { id: "15", text: "I need medicine", acronym: "inm", language: "en", frequency: 30 },

  // Hebrew phrases
  { id: "16", text: "אני רוצה לשתות", acronym: "ארל", language: "he", frequency: 100 },
  { id: "17", text: "אני צריך עזרה", acronym: "אצע", language: "he", frequency: 95 },
  { id: "18", text: "תודה רבה", acronym: "תר", language: "he", frequency: 90 },
  { id: "19", text: "מה נשמע", acronym: "מנ", language: "he", frequency: 85 },
  { id: "20", text: "אני אוהב אותך", acronym: "אאא", language: "he", frequency: 80 },
  { id: "21", text: "בוקר טוב", acronym: "בט", language: "he", frequency: 75 },
  { id: "22", text: "לילה טוב", acronym: "לט", language: "he", frequency: 70 },
  { id: "23", text: "אני רוצה לאכול", acronym: "ארל", language: "he", frequency: 65 },
  { id: "24", text: "כואב לי", acronym: "כל", language: "he", frequency: 60 },
  { id: "25", text: "בבקשה חכה", acronym: "בח", language: "he", frequency: 55 },
  { id: "26", text: "אני עייף", acronym: "אע", language: "he", frequency: 50 },
  { id: "27", text: "כן בבקשה", acronym: "כב", language: "he", frequency: 45 },
  { id: "28", text: "לא תודה", acronym: "לת", language: "he", frequency: 40 },
  { id: "29", text: "תקרא למשפחה", acronym: "תל", language: "he", frequency: 35 },
  { id: "30", text: "אני צריך תרופות", acronym: "אצת", language: "he", frequency: 30 },

  // Russian phrases
  { id: "31", text: "Я хочу воды", acronym: "яхв", language: "ru", frequency: 100 },
  { id: "32", text: "Мне нужна помощь", acronym: "мнп", language: "ru", frequency: 95 },
  { id: "33", text: "Спасибо", acronym: "с", language: "ru", frequency: 90 },
  { id: "34", text: "Как дела", acronym: "кд", language: "ru", frequency: 85 },
  { id: "35", text: "Я люблю тебя", acronym: "ялт", language: "ru", frequency: 80 },
  { id: "36", text: "Доброе утро", acronym: "ду", language: "ru", frequency: 75 },
  { id: "37", text: "Спокойной ночи", acronym: "сн", language: "ru", frequency: 70 },
  { id: "38", text: "Я хочу есть", acronym: "яхе", language: "ru", frequency: 65 },
  { id: "39", text: "Мне больно", acronym: "мб", language: "ru", frequency: 60 },
  { id: "40", text: "Пожалуйста подождите", acronym: "пп", language: "ru", frequency: 55 },

  // Arabic phrases
  { id: "41", text: "أريد ماء", acronym: "ام", language: "ar", frequency: 100 },
  { id: "42", text: "أحتاج مساعدة", acronym: "ام", language: "ar", frequency: 95 },
  { id: "43", text: "شكراً", acronym: "ش", language: "ar", frequency: 90 },
  { id: "44", text: "كيف حالك", acronym: "كح", language: "ar", frequency: 85 },
  { id: "45", text: "أحبك", acronym: "ا", language: "ar", frequency: 80 },
  { id: "46", text: "صباح الخير", acronym: "صا", language: "ar", frequency: 75 },
  { id: "47", text: "تصبح على خير", acronym: "تعخ", language: "ar", frequency: 70 },
  { id: "48", text: "أريد طعام", acronym: "اط", language: "ar", frequency: 65 },
  { id: "49", text: "أشعر بألم", acronym: "اب", language: "ar", frequency: 60 },
  { id: "50", text: "انتظر من فضلك", acronym: "امف", language: "ar", frequency: 55 },
];

const STORAGE_KEY = "aac_dictionary";

export function loadDictionary(): DictionaryEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to load dictionary:", e);
  }
  // Initialize with seed if nothing stored
  saveDictionary(seedDictionary);
  return seedDictionary;
}

export function saveDictionary(dictionary: DictionaryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dictionary));
  } catch (e) {
    console.error("Failed to save dictionary:", e);
  }
}

export function addToDictionary(text: string): DictionaryEntry {
  const dictionary = loadDictionary();
  const newEntry: DictionaryEntry = {
    id: Date.now().toString(),
    text: text.trim(),
    acronym: generateAcronym(text.trim()),
    language: detectLanguage(text),
    frequency: 1,
  };
  dictionary.push(newEntry);
  saveDictionary(dictionary);
  return newEntry;
}

// Search dictionary for phrases in standard mode (startsWith)
export function searchStandard(query: string, dictionary: DictionaryEntry[]): DictionaryEntry[] {
  if (!query.trim()) return [];
  const lowerQuery = query.toLowerCase();
  const lang = detectLanguage(query);

  return dictionary
    .filter(
      (entry) =>
        entry.language === lang && entry.text.toLowerCase().startsWith(lowerQuery)
    )
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, 6);
}

// Search dictionary for phrases in acronym mode (matches first letters)
export function searchAcronym(query: string, dictionary: DictionaryEntry[]): DictionaryEntry[] {
  if (!query.trim()) return [];
  const lowerQuery = query.toLowerCase().replace(/\s/g, "");
  const lang = detectLanguage(query);

  return dictionary
    .filter((entry) => entry.language === lang && entry.acronym.startsWith(lowerQuery))
    .sort((a, b) => b.frequency - a.frequency)
    .slice(0, 6);
}

/**
 * Search vocabulary for word predictions (NEW - uses vocabulary files)
 * This is the main prediction function for single-word completion
 */
export function searchWords(query: string, limit: number = 6): string[] {
  if (!query.trim()) return [];
  
  const lang = detectLanguage(query);
  return searchVocabulary(query, lang, limit);
}

/**
 * Get word predictions from the last word being typed
 */
export function getWordPredictions(text: string, limit: number = 6): string[] {
  if (!text.trim()) return [];

  const words = text.split(" ");
  const lastWord = words[words.length - 1];

  if (!lastWord) return [];

  return searchWords(lastWord, limit);
}
